import React, { useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { API_BASE_URL, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";

type CreditsModalProps = {
  open: boolean;
  onClose: () => void;
  baseRate: number; // credits per $5, e.g. 100
  onAddCredits?: (credits: number) => void;
};

export const CreditsModal: React.FC<CreditsModalProps> = ({
  open,
  onClose,
  baseRate,
  onAddCredits,
}) => {
  const [customAmount, setCustomAmount] = useState<string>("");
  const minAmount = 5;
  const [selectedPackIndex, setSelectedPackIndex] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const parseAmount = (value: string) => {
    const num = Number(value.replace(",", "."));
    return Number.isFinite(num) ? num : 0;
  };

  const currentAmount = parseAmount(customAmount);
  const isCustomValid = currentAmount >= minAmount;
  const computeCredits = (amountUsd: number) => {
    const perDollar = baseRate / 5; // e.g. 120 / 5 = 24 credits per $1
    const total = Math.round(amountUsd * perDollar);

    // From $40 and above — show +20% bonus, where bonus is 20% of total value
    if (amountUsd >= 40) {
      const bonus = Math.round(total * 0.2);
      const base = total - bonus;
      return { base, bonus, total };
    }

    return { base: total, bonus: 0, total };
  };

  const effectiveAmount = isCustomValid ? currentAmount : minAmount;
  const customInfo = computeCredits(effectiveAmount);
  const displayAmount = isCustomValid ? effectiveAmount.toFixed(0) : String(minAmount);
  const displayTotalCredits = customInfo.total;
  const displayBonusCredits = customInfo.bonus;

  const presetPackages = [
    { amount: 5, label: "$5" },
    { amount: 10, label: "$10" },
    { amount: 20, label: "$20" },
    { amount: 40, label: "$40" }, // 20% bonus will apply here
  ];

  const selectedPack =
    selectedPackIndex !== null ? presetPackages[selectedPackIndex] : null;

  const canBuyFromSelection = !!selectedPack || isCustomValid;

  const startCheckout = async (amountUsd: number) => {
    if (amountUsd < minAmount || isProcessing) return;

    const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
    if (!token) {
      setError("Please sign in again to buy credits.");
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const response = await axios.post(
        `${API_BASE_URL}/billing/credits/checkout`,
        { amount_usd: amountUsd },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const checkoutUrl = response?.data?.data?.checkout_url;
      if (checkoutUrl && typeof checkoutUrl === "string") {
        // Optional callback hook before redirect
        if (onAddCredits) {
          try {
            onAddCredits(0);
          } catch {
            // ignore
          }
        }
        window.location.href = checkoutUrl;
      } else {
        setError("Unable to start payment. Please try again.");
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        "Failed to start payment. Please try again.";
      setError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBuySelected = () => {
    if (!canBuyFromSelection || isProcessing) return;
    if (selectedPack) {
      startCheckout(selectedPack.amount);
    } else if (isCustomValid) {
      startCheckout(currentAmount);
    }
  };

  const handleBuyPackDirect = (amountUsd: number) => {
    if (isProcessing) return;
    startCheckout(amountUsd);
  };

  return (
    <CreditsOverlay
      onClick={() => {
        onClose();
      }}
    >
      <CreditsDialog
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <CreditsDialogHeader>
          <CreditsDialogTitle>Top up credits</CreditsDialogTitle>
          <CreditsDialogSubtitle>
            5&nbsp;$ = {baseRate} credits. Choose a pack or enter your own amount.
          </CreditsDialogSubtitle>
        </CreditsDialogHeader>
        <CreditsPacksGrid>
          {presetPackages.map((pack, index) => {
            const info = computeCredits(pack.amount);
            const hasBonus = info.bonus > 0;
            return (
              <CreditsPackCard
                key={pack.label}
                type="button"
                $selected={selectedPackIndex === index}
                onClick={() =>
                  setSelectedPackIndex((prev) => (prev === index ? null : index))
                }
              >
                <CreditsPackHeader>
                  <CreditsPackAmount>
                    <span>{pack.label}</span>
                  </CreditsPackAmount>
                  {hasBonus && <DiscountBadge>+20% bonus</DiscountBadge>}
                </CreditsPackHeader>
                <CreditsPackCredits>
                  {hasBonus
                    ? `${info.base} credits (+${info.bonus} bonus)`
                    : `${info.base} credits`}
                </CreditsPackCredits>
                <CreditsPackButton
                  type="button"
                  disabled={isProcessing}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBuyPackDirect(pack.amount);
                  }}
                >
                  
                    {info.total} credits for {pack.label}
                </CreditsPackButton>
              </CreditsPackCard>
            );
          })}
        </CreditsPacksGrid>
        <CreditsCustomSection>
          <CreditsCustomLabel>
            Custom amount
            <span>(min ${minAmount})</span>
          </CreditsCustomLabel>
          <CreditsCustomRow>
            <CreditsCustomInputWrapper>
              <span>$</span>
              <input
                type="number"
                min={minAmount}
                step="1"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  if (selectedPackIndex !== null) {
                    setSelectedPackIndex(null);
                  }
                }}
                placeholder={String(minAmount)}
              />
            </CreditsCustomInputWrapper>
            <CreditsCustomSummary $invalid={!isCustomValid && customAmount !== ""}>
              {customAmount === "" ? (
                "Enter amount to see credits"
              ) : !isCustomValid ? (
                `Minimum top up is $${minAmount}`
              ) : null}
            </CreditsCustomSummary>
          </CreditsCustomRow>
          <CreditsCustomButton
            type="button"
            disabled={!canBuyFromSelection || isProcessing}
            onClick={handleBuySelected}
          >
            <span>
              Buy{" "}
              {selectedPack
                ? (() => {
                    const info = computeCredits(selectedPack.amount);
                    return `${info.total} credits for $${selectedPack.amount}`;
                  })()
                : `${displayTotalCredits} credits for $${displayAmount}`}
            </span>
            {selectedPack
              ? (() => {
                  const info = computeCredits(selectedPack.amount);
                  return info.bonus > 0 ? (
                    <DiscountBadge>+20% bonus</DiscountBadge>
                  ) : null;
                })()
              : displayBonusCredits > 0 && (
                  <DiscountBadge>+20% bonus</DiscountBadge>
                )}
          </CreditsCustomButton>
          {error && <CreditsError>{error}</CreditsError>}
        </CreditsCustomSection>
      </CreditsDialog>
    </CreditsOverlay>
  );
};

const CreditsOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const CreditsDialog = styled.div`
  width: 420px;
  max-width: 90vw;
  border-radius: 24px;
  background: #ffffff;
  padding: 24px 24px 20px 24px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.4);
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    margin: 0 16px;
  }
`;

const CreditsDialogHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CreditsDialogTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const CreditsDialogSubtitle = styled.div`
  font-size: 13px;
  color: #4b5563;
`;

const CreditsPacksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const CreditsPackCard = styled.button<{ $selected?: boolean }>`
  border-radius: 16px;
  border: 1.5px solid ${(p) => (p.$selected ? "#22c55e" : "#e5e7eb")};
  background: ${(p) => (p.$selected ? "#ecfdf3" : "#f9fafb")};
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease,
    box-shadow 0.2s ease, transform 0.1s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
  }
`;

const CreditsPackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
`;

const CreditsPackAmount = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OriginalPrice = styled.span`
  font-size: 12px;
  color: #9ca3af;
  text-decoration: line-through;
`;

const DiscountPrice = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const DiscountBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #0f766e;
  background: #d1fae5;
  border-radius: 999px;
  padding: 2px 8px;
`;

const CreditsPackCredits = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
`;

const CreditsPackButton = styled.button`
  margin-top: 6px;
  border-radius: 999px;
  border: none;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  cursor: pointer;
  width: 100%;
  box-shadow: 0 6px 14px rgba(248, 113, 113, 0.3);
  transition: transform 0.1s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(248, 113, 113, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 6px 14px rgba(248, 113, 113, 0.3);
  }
`;

const CreditsCustomSection = styled.div`
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CreditsCustomLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #111827;

  span {
    margin-left: 6px;
    font-size: 12px;
    font-weight: 400;
    color: #6b7280;
  }
`;

const CreditsCustomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CreditsCustomInputWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;

  span {
    font-size: 13px;
    color: #4b5563;
  }

  input {
    width: 70px;
    border: none;
    background: transparent;
    font-size: 13px;
    color: #111827;
    outline: none;
  }
`;

const CreditsCustomSummary = styled.div<{ $invalid?: boolean }>`
  font-size: 12px;
  color: ${(p) => (p.$invalid ? "#b91c1c" : "#4b5563")};

  strong {
    font-weight: 600;
  }
`;

const CreditsError = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #b91c1c;
`;

const CreditsCustomButton = styled.button`
  align-self: stretch;
  margin-top: 8px;
  border-radius: 12px;
  border: none;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  color: #ffffff;
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  box-shadow: 0 8px 20px rgba(248, 113, 113, 0.35);
  transition: transform 0.1s ease, box-shadow 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  & > span:first-child {
    flex: 1;
    text-align: center;
  }

  &:hover {
    transform: ${(p) => (p.disabled ? "none" : "translateY(-1px)")};
    box-shadow: ${(p) =>
      p.disabled
        ? "0 8px 20px rgba(248, 113, 113, 0.35)"
        : "0 10px 24px rgba(248, 113, 113, 0.45)"};
  }
`;


